import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCompanyId, setActiveCompanyId] = useState(localStorage.getItem("activeCompanyId") || null);

  const pickDefaultCompany = (u) => {
    const stored = localStorage.getItem("activeCompanyId");
    const ids = (u.companies || []).map((c) => c.id);
    const chosen = stored && ids.includes(stored) ? stored : ids[0] || null;
    if (chosen) localStorage.setItem("activeCompanyId", chosen);
    else localStorage.removeItem("activeCompanyId");
    setActiveCompanyId(chosen);
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((res) => {
        setUser(res.data);
        pickDefaultCompany(res.data);
      })
      .catch(() => {
        localStorage.removeItem("token");
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    localStorage.setItem("token", res.data.token);
    setUser(res.data.user);
    pickDefaultCompany(res.data.user);
    return res.data.user;
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("activeCompanyId");
    setUser(null);
    setActiveCompanyId(null);
  };

  const selectCompany = (id) => {
    localStorage.setItem("activeCompanyId", id);
    setActiveCompanyId(id);
  };

  const refreshUser = async () => {
    const res = await api.get("/auth/me");
    setUser(res.data);
    pickDefaultCompany(res.data);
    return res.data;
  };

  const companies = user?.companies || [];
  const activeCompany = companies.find((c) => c.id === activeCompanyId) || null;
  const activeRole = activeCompany?.role || null;
  const isSuperadmin = !!user?.is_superadmin;
  const canEdit = ["superadmin", "company_admin", "editor"].includes(activeRole);
  const canDelete = ["superadmin", "company_admin"].includes(activeRole);
  const isManager = isSuperadmin || companies.some((c) => c.role === "company_admin");

  const LEVEL_RANK = { none: 0, view: 1, edit: 2, full: 3 };
  const moduleLevel = (mod) => {
    if (isSuperadmin || activeRole === "company_admin") return "full";
    const perms = activeCompany?.permissions || {};
    if (perms[mod]) return perms[mod];
    if (activeRole === "editor") return "edit";
    if (activeRole === "viewer") return "view";
    return "none";
  };
  const can = (mod, action) => {
    const need = { view: 1, edit: 2, delete: 3 }[action] || 1;
    return (LEVEL_RANK[moduleLevel(mod)] || 0) >= need;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        companies,
        activeCompany,
        activeCompanyId,
        selectCompany,
        activeRole,
        canEdit,
        canDelete,
        moduleLevel,
        can,
        isSuperadmin,
        isManager,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
